// Iter60 — Manual BCCS Scanner Panel.
//
// Read-only UI that lets the user POST a single JSON body to the
// Iter59 backend scanner: POST /api/forge/toolbox/scanner/bccs-preview.
// One backend call per click. No auto-fire on load, no multi-endpoint
// fanout, no broker / journal / DB / live market fetch.
//
// Bearish/resistance-side mirror of the Iter58 BPCS Scanner Panel
// — strictly isolated, never references any other module endpoint.

import React, { useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ChevronDown, ChevronUp, Play, Search,
  ShieldAlert, CheckCircle2, Eraser,
} from "lucide-react";
import { API } from "@/App";
import ScannerResultActions from "@/components/ScannerResultActions";
import FriendlyScannerForm from "@/components/FriendlyScannerForm";
import StrategyResultCard from "@/components/StrategyResultCard";
import { ScannerSpreadMetrics } from "@/components/ScannerResultNormalization";

// Single allowed endpoint — hardcoded, never derived from input.
const SCANNER_ENDPOINT = "/forge/toolbox/scanner/bccs-preview";

const DEFAULT_BODY = {
  watchlist: ["SPY", "QQQ"],
  payloads_by_symbol: {
    SPY: {
      spot_price: 505.0,
      wes: {
        val: 495.0, vah: 508.0, poc: 502.0,
        wes_p: 6, wes_c: 8, delta_VAH: -0.5,
        price_location: "reject_from_vah",
        closed_bar_confirmed: true,
      },
      gex: { call_wall: 512.0 },
      chain: [
        { type: "call", strike: 505.0, bid: 4.10, ask: 4.15,
          delta: 0.50, open_interest: 5000, volume: 200 },
        { type: "call", strike: 510.0, bid: 1.50, ask: 1.55,
          delta: 0.22, open_interest: 5000, volume: 200 },
        { type: "call", strike: 515.0, bid: 0.42, ask: 0.45,
          delta: 0.10, open_interest: 4000, volume: 150 },
      ],
      expiration: "2026-06-21", dte: 28, ivr: 35,
      wheel_engine: { actionable: false },
      account_profile: { account_value: 100000,
                          max_risk_per_trade_pct: 0.01 },
    },
    QQQ: {
      spot_price: 400.0,
      wes: { val: 395.0, vah: 410.0, poc: 400.0,
              wes_p: 6, wes_c: 6,
              closed_bar_confirmed: false },
      chain: [],
    },
  },
};

const STATUS_BADGE = {
  ENTER:    "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]",
  WATCH:    "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  BLOCKED:  "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  NO_TRADE: "border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93]",
  ERROR:    "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
};

export default function BCCSScannerPanel() {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(JSON.stringify(DEFAULT_BODY, null, 2));
  const [parseErr, setParseErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);

  async function runScanner() {
    setErr(null);
    setParseErr(null);
    let parsed;
    try {
      parsed = JSON.parse(body || "{}");
    } catch (e) {
      setParseErr(`Invalid JSON: ${e.message}`);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}${SCANNER_ENDPOINT}`, parsed);
      setResult(r.data);
      setLastRequest(parsed);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  function resetBody() {
    setBody(JSON.stringify(DEFAULT_BODY, null, 2));
    setParseErr(null);
  }

  function clearResult() {
    setResult(null);
    setErr(null);
  }

  return (
    <div
      data-testid="bccs-scanner-panel"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      <button
        data-testid="bccs-scanner-toggle"
        onClick={() => setOpen(v => !v)}
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Search size={16} className="text-[#FF3366]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Bear Call Credit Spread Watchlist Scanner
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · Iter59 backend · manual-run-per-click · resistance rejection
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* ── Safety banner ───────────────────────────────────── */}
          <div
            data-testid="bccs-scanner-banner"
            className="px-4 py-2 border-b border-[#26262B] bg-[#FFB800]/5 text-[#FFB800] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            <span data-testid="bccs-scanner-banner-bccs-only">
              BCCS scanner only.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bccs-scanner-banner-readonly">
              Read-only opportunity discovery.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bccs-scanner-banner-no-execution">
              No trade execution.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bccs-scanner-banner-prebuilt-payloads">
              Requires prebuilt payloads.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bccs-scanner-banner-defined-risk">
              Defined-risk only · Call-side premium.
            </span>
          </div>

          {/* ── Body editor + run controls ───────────────────────── */}
          <div className="px-4 py-3 border-b border-[#26262B]">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                Request body
              </span>
              <span className="text-[9px] font-mono text-[#5F5F66] truncate"
                    title={SCANNER_ENDPOINT}>
                {SCANNER_ENDPOINT}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  data-testid="bccs-scanner-reset"
                  onClick={resetBody}
                  className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                >
                  <Eraser size={10} /> Reset
                </button>
                <button
                  type="button"
                  data-testid="bccs-scanner-run"
                  onClick={runScanner}
                  disabled={loading}
                  className="px-3 py-1 rounded-sm border border-[#FF3366]/40 bg-[#FF3366]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#FF3366] hover:bg-[#FF3366]/20 disabled:opacity-40 inline-flex items-center gap-1"
                >
                  <Play size={10} /> {loading ? "Scanning…" : "Run BCCS Scanner"}
                </button>
              </div>
            </div>

            <FriendlyScannerForm
              module="BCCS"
              body={body}
              setBody={setBody}
              parseErr={parseErr}
            />

            <details
              data-testid="bccs-scanner-advanced-payload"
              className="border border-[#26262B] rounded-sm"
            >
              <summary
                data-testid="bccs-scanner-advanced-summary"
                className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]"
              >
                Advanced / Developer payload (raw JSON)
              </summary>
              <textarea
                data-testid="bccs-scanner-payload"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                spellCheck={false}
                className="w-full bg-[#08080A] border-t border-[#26262B] p-2 text-[10px] font-mono text-[#E0E0E0] focus:outline-none focus:border-[#FF3366]/50 leading-relaxed"
                rows={8}
              />
            </details>

            {parseErr && (
              <div
                data-testid="bccs-scanner-parse-error"
                className="mt-2 flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1.5 rounded-sm"
              >
                <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                <span>{parseErr}</span>
              </div>
            )}
          </div>

          {/* ── Result render ───────────────────────────────────── */}
          {loading && (
            <div
              data-testid="bccs-scanner-loading"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              Running BCCS scanner…
            </div>
          )}

          {err && !loading && (
            <div
              data-testid="bccs-scanner-error"
              className="px-4 py-3 flex items-start gap-2 text-[10px] font-mono text-[#FF3366]"
            >
              <AlertTriangle size={11} className="shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          {!loading && !err && !result && (
            <div
              data-testid="bccs-scanner-empty"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No scan run yet. Edit the request body above and click
              Run BCCS Scanner.
            </div>
          )}

          {result && !loading && !err && (
            <ScannerResult result={result} onClear={clearResult} lastRequest={lastRequest} />
          )}
        </div>
      )}
    </div>
  );
}

function ScannerResult({ result, onClear, lastRequest }) {
  const counts = result.counts || {};
  return (
    <div
      data-testid="bccs-scanner-result"
      className="px-4 py-3"
    >
      <div className="flex items-center gap-2 flex-wrap mb-3 text-[10px] font-mono">
        <CheckCircle2 size={11} className="text-[#00FF88]" />
        <span
          data-testid="bccs-scanner-result-version"
          className="text-[#8A8A93] uppercase tracking-[0.12em]"
        >
          {result.scanner_version}
        </span>
        <span
          data-testid="bccs-scanner-result-module-id"
          className="px-2 py-0.5 rounded-sm border border-[#FF3366]/40 bg-[#FF3366]/10 text-[9px] uppercase tracking-[0.14em] text-[#FF3366]"
        >
          {result.module_id}
        </span>
        <span
          data-testid="bccs-scanner-result-total"
          className="text-[#E0E0E0]"
        >
          {result.total_symbols} symbols scanned
        </span>
        <span className="ml-auto inline-flex items-center gap-3">
          {["enter", "watch", "blocked", "no_trade", "error"].map(k => (
            <span
              key={k}
              data-testid={`bccs-scanner-result-count-${k}`}
              className="flex items-center gap-1 text-[#8A8A93]"
            >
              <span className="uppercase tracking-[0.12em]">{k}</span>
              <span className="font-bold text-[#E0E0E0]">{counts[k] ?? 0}</span>
            </span>
          ))}
          <button
            type="button"
            data-testid="bccs-scanner-result-clear"
            onClick={onClear}
            className="text-[#5F5F66] hover:text-[#E0E0E0] uppercase tracking-[0.12em]"
          >
            Clear
          </button>
          <ScannerResultActions
            result={result}
            request={lastRequest}
            endpoint={SCANNER_ENDPOINT}
            sourcePanel="BCCSScannerPanel"
            moduleId={result.module_id || "BCCS"}
            testidPrefix="bccs-scanner-result"
          />
        </span>
      </div>

      {Array.isArray(result.warnings) && result.warnings.length > 0 && (
        <div
          data-testid="bccs-scanner-result-warnings"
          className="mb-3 text-[10px] font-mono text-[#FFB800]"
        >
          {result.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle size={10} className="mt-0.5 shrink-0" />
              {w}
            </div>
          ))}
        </div>
      )}

      <ul className="space-y-2">
        {(result.results || []).map(rec => (
          <StrategyResultCard
            key={rec.symbol}
            rec={rec}
            moduleLabel="BCCS"
            accent={{
              border: "border-[#FF3366]/30",
              glow:   "bg-[#FF3366]/5",
              text:   "text-[#FF3366]",
            }}
            testidPrefix="bccs-scanner-result-row"
          >
            <ScannerSpreadMetrics
              rec={rec}
              module="BCCS"
              testidPrefix="bccs-scanner-result-row"
            />
          </StrategyResultCard>
        ))}
      </ul>
    </div>
  );
}
